/**
 * Ölçüm ekranı.
 *
 * Akış: kalibrasyon → motor bir deneme verir → uyarıcı gizli olarak
 * çizilir → rAF ile görünür olur → tepki alınır → motora geri verilir.
 * Ekran hiçbir puanlama yapmaz; yalnızca gösterir ve ölçer.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AssessmentSession,
  STALL_TIMEOUT_MS,
  judge,
  practiceTrials,
  BIRIM,
  DOMAIN_LABEL,
  DOMAIN_ORDER,
  KISITLAR,
  TASK_ADI,
  bicim,
  metricLabel,
  yorumla,
  type ScoreRow,
  type TrialSpec,
  type TaskCode,
} from '@nirengi/engine'
import { calibrate, deviceInfo, runTrial, type Calibration, type TrialRun } from '../timing'
import { SHAPE_GLYPH, Stimulus, SearchTarget } from '../components/Stimulus'
import { Sunucu, jetonuOku, type OlcumBilgisi } from '../api'

type Phase = 'calibrating' | 'intro' | 'running' | 'gonderiliyor' | 'done'

const TASK_LABEL: Record<TaskCode, string> = {
  cpt: 'Dikkatini sürdür',
  flanker: 'Ortadaki oka bak',
  span_visual: 'Sırayı hatırla',
  nback: 'Geriye bak',
  symbol_search: 'Şekli ara',
  switching: 'Kurala uy',
}

/**
 * Görev anlatımları.
 *
 * Çocuğa yazılıyor, uygulayıcıya değil. İlk hali bir yetişkine not gibi
 * tek cümleydi ("Yazan kurala göre seç") ve bir çocuğun o cümleden
 * görevi anlaması mümkün değildi. Anlamayan çocuk görevi deneyerek
 * çözmeye çalışır; ilk denemeler bu yüzden dikkati değil çözme çabasını
 * ölçer.
 *
 * Her görev için: tek cümlelik kural, adım adım ne yapacağı, somut bir
 * örnek ve işin zor tarafı.
 */
interface Anlatim {
  kisa: string
  adimlar: string[]
  ornek: string
  dikkat: string
}

const TASK_ANLATIM: Record<TaskCode, Anlatim> = {
  cpt: {
    kisa: 'Sadece X harfini görünce bas.',
    adimlar: [
      'Ekranda harfler tek tek gelecek: B, M, K, X gibi.',
      'Sadece X harfini gördüğünde BAS düğmesine bas.',
      'Başka bir harf geldiğinde hiçbir şey yapma, bekle.',
      'Harfler durmadan gelmeye devam edecek. Sıkılsan da ekrandan gözünü ayırma.',
    ],
    ornek: 'B geldi → bekle. M geldi → bekle. X geldi → BAS!',
    dikkat: 'X seyrek gelir. İşin zor kısmı beklerken dalmamak.',
  },
  flanker: {
    kisa: 'Ortadaki okun yönünü seç.',
    adimlar: [
      'Ekranda yan yana birkaç ok görünecek.',
      'Sadece ORTADAKİ oka bak, diğerlerini görmezden gel.',
      'Ortadaki sola bakıyorsa ◀ düğmesine bas.',
      'Ortadaki sağa bakıyorsa ▶ düğmesine bas.',
    ],
    ornek: '▶ ▶ ◀ ▶ ▶ → ortadaki sola bakıyor, ◀ düğmesine basacaksın.',
    dikkat: 'Yanındaki oklar bazen ters yöne bakar ve seni şaşırtmaya çalışır.',
  },
  span_visual: {
    kisa: 'Yanan kutuları sırayla izle, sonra aynı sırayla dokun.',
    adimlar: [
      'Kutulardan bazıları tek tek yanıp sönecek.',
      'Hangisinin önce, hangisinin sonra yandığını aklında tut.',
      'Yanmalar bitince sıra sende: kutulara AYNI SIRAYLA dokun.',
      'Doğru yaptıkça diziye bir kutu daha eklenecek.',
    ],
    ornek: 'Önce sol üst, sonra sağ alt yandıysa: önce sol üste, sonra sağ alta dokun.',
    dikkat: 'Doğru kutulara ama yanlış sırayla dokunursan sayılmaz. Sıra önemli.',
  },
  // n-back'in kuralı oyun sırasında değiştiği için metni nbackKural üretir
  nback: {
    kisa: '',
    adimlar: [],
    ornek: '',
    dikkat: '',
  },
  symbol_search: {
    kisa: 'Aradığın şekil tabloda var mı?',
    adimlar: [
      'En üstte aradığın şekil gösterilecek.',
      'Altındaki tabloya bak ve o şekli ara.',
      'Tabloda varsa VAR düğmesine bas.',
      'Tabloda yoksa YOK düğmesine bas.',
    ],
    ornek: 'Aradığın şekil ▲ ve tabloda bir ▲ görüyorsan → VAR.',
    dikkat: 'Şekiller birbirine benziyor. Hızlı ol ama acele edip yanlış basma.',
  },
  switching: {
    kisa: 'Üstte yazan kurala göre seç: RENK yazıyorsa rengi, ŞEKİL yazıyorsa şekli.',
    adimlar: [
      'Ekranda renkli bir şekil görünecek. Hem rengi hem şekli var.',
      'Şeklin üstünde büyük harflerle KURAL yazacak: ya RENK ya ŞEKİL.',
      'RENK yazıyorsa, aşağıdaki renklerden o rengi seç.',
      'ŞEKİL yazıyorsa, aşağıdaki şekillerden o şekli seç.',
      'Kural sık sık değişecek. Her seferinde üstteki yazıya tekrar bak.',
    ],
    ornek: 'Mavi bir yıldız var. Üstte RENK yazıyorsa maviyi seç, ŞEKİL yazıyorsa yıldızı seç.',
    dikkat: 'En çok hata kural değiştiğinde olur: eski kurala göre devam etmek kolay.',
  },
}

/**
 * n-back kuralının o anki hali.
 *
 * Eski metin "birkaç adım öncekiyle aynıysa bas" diyordu. İki sorunu
 * vardı: "birkaç" belirsiz, ve asıl önemlisi SAYI OYUN SIRASINDA
 * DEĞİŞİYOR — merdiven 1'den 4'e kadar çıkarıyor. Çocuğa söylenmeyince
 * görev imkânsız hale geliyor ve ölçtüğümüz şey belleği değil, kuralı
 * tahmin etme çabasını oluyor.
 */
function nbackKural(n: number): {
  kisa: string
  uzun: string
  ornek: string
  tut: string
} {
  /**
   * Kaç harf akılda tutulacağı AÇIKÇA söyleniyor.
   *
   * Bu bir ipucu değil, görevin tanımı. n-back, kuralı bilen bir kişinin
   * belleğini ölçer; kuralı saklamak görevi "kuralı tahmin etme" oyununa
   * çevirir ve ölçülen şey bellek olmaktan çıkar. Standart uygulamalarda
   * da katılımcıya düzey (1-back, 2-back) söylenir.
   *
   * Saklanması gereken şey başka: hedefin ne sıklıkta geleceği ve dizinin
   * kendisi. Onlar söylenmiyor.
   */
  const tut = `Aklında son ${n} harfi tut.`

  if (n === 1) {
    return {
      kisa: 'Harf, BİR ÖNCEKİ harfle aynıysa bas.',
      uzun:
        'Harfler tek tek gelecek. Her harf geldiğinde bir öncekini düşün: ' +
        'aynı harf üst üste iki kez geldiyse bas, gelmediyse hiçbir şey yapma.',
      ornek: 'B → K → K ← burada bas (K, bir öncekiyle aynı)',
      tut: 'Aklında sadece son harfi tut.',
    }
  }
  return {
    kisa: `Harf, ${n} ADIM ÖNCEKİ harfle aynıysa bas.`,
    uzun:
      `Harfler tek tek gelecek. Her harf geldiğinde ${n} adım öncekini hatırla: ` +
      `aynıysa bas, değilse hiçbir şey yapma. Aradaki ${n === 2 ? 'harf' : 'harfler'} önemli değil.`,
    ornek:
      n === 2
        ? 'B → K → B ← burada bas (B, 2 adım öncekiyle aynı)'
        : n === 3
          ? 'B → K → M → B ← burada bas (B, 3 adım öncekiyle aynı)'
          : 'B → K → M → T → B ← burada bas (B, 4 adım öncekiyle aynı)',
    tut,
  }
}

function hintFor(spec: TrialSpec | null): string {
  if (!spec) return ''
  if (spec.task === 'nback') return nbackKural(spec.level).kisa
  return TASK_ANLATIM[spec.task].kisa
}

/** n-back dışındaki görevler için sabit, n-back için o anki kurala göre */
function anlatimFor(task: TaskCode, n = 1): Anlatim {
  if (task !== 'nback') return TASK_ANLATIM[task]
  const k = nbackKural(n)
  return {
    kisa: k.kisa,
    adimlar: [
      'Ekranda harfler tek tek gelecek.',
      k.uzun,
      k.tut,
      'Sen doğru yaptıkça kural zorlaşacak; her değiştiğinde ekranda yazacak.',
    ],
    ornek: k.ornek,
    dikkat:
      'Aradaki harfleri de görürsün ama onlara basmayacaksın. Sadece kuralın söylediği harfe bas.',
  }
}

export function Assessment({ onExit }: { onExit: () => void }) {
  const [phase, setPhase] = useState<Phase>('calibrating')
  const [cal, setCal] = useState<Calibration | null>(null)
  const [spec, setSpec] = useState<TrialSpec | null>(null)
  const [scores, setScores] = useState<ScoreRow[] | null>(null)
  const [summary, setSummary] = useState<{ trials: number; invalidRatio: number; ok: boolean } | null>(null)
  const [picked, setPicked] = useState<string[]>([])
  const [flash, setFlash] = useState<number | null>(null)
  const [progress, setProgress] = useState({ done: 0 })
  /** Kare akışı durduğunda ölçüm bekler ve kullanıcıya haber verir */
  const [paused, setPaused] = useState(false)
  const resumeRef = useRef<(() => void) | null>(null)

  /**
   * Görev başına akış: tanıtım → alıştırma → hazır ol → geri sayım.
   *
   * Doğrudan gerçek denemelerle başlamak, ölçümün başını sistemli olarak
   * bozuyordu: çocuk ilk denemelerde görevi öğreniyor ve o denemeler
   * dikkati değil öğrenmeyi ölçüyor.
   */
  const [gate, setGate] = useState<
    | { kind: 'intro'; task: TaskCode }
    | { kind: 'practice'; task: TaskCode }
    | { kind: 'ready'; task: TaskCode; correct: number; total: number }
    | { kind: 'kuraldegisti'; n: number }
    | null
  >(null)
  const [feedback, setFeedback] = useState<{ ok: boolean; text?: string } | null>(null)
  const [countdown, setCountdown] = useState<number | null>(null)
  /**
   * Tepki alındı işareti.
   *
   * Basıp hiçbir şey olmayınca çocuk basışının kaydedilip kaydedilmediğini
   * bilemiyor ve tekrar basıyor — bu hem ölçümü bozar hem sinir bozucu.
   * Kısa bir duraklama ve işaret, basışın kaydedildiğini söylüyor.
   *
   * DOĞRULUK bilgisi vermiyor: gerçek blokta "doğru yaptın" demek
   * çocuğun stratejisini değiştirir ve ölçtüğümüz şeyi bozar. Pekiştirme
   * alıştırmaya ait.
   */
  const [ack, setAck] = useState(false)
  const gateRef = useRef<(() => void) | null>(null)
  const saltRef = useRef('demo-' + Date.now())
  const startedAtRef = useRef(Date.now())

  const sessionRef = useRef<AssessmentSession | null>(null)
  const stimRef = useRef<HTMLDivElement>(null)
  const pickedRef = useRef<string[]>([])

  /* ---------------- sunucu bağlantısı ---------------- */
  const sunucuRef = useRef<Sunucu | null>(null)
  const [bagli, setBagli] = useState<OlcumBilgisi | null>(null)
  const [jetonHatasi, setJetonHatasi] = useState(false)
  const [gonderim, setGonderim] = useState<{ tamam: boolean; eksik: number } | null>(null)
  const [gonderimDurumu, setGonderimDurumu] = useState<{ bekleyen: number; bagli: boolean }>({
    bekleyen: 0,
    bagli: true,
  })

  /* ---------------- kalibrasyon ---------------- */
  useEffect(() => {
    let alive = true

    const hazirla = async () => {
      /**
       * Adreste jeton varsa bağlı mod. Jeton psikolog ölçümü başlatırken
       * üretiliyor; çocuk hesap açmıyor, parola girmiyor.
       */
      const jeton = jetonuOku()
      if (jeton) {
        const s = new Sunucu(jeton)
        const bilgi = await s.baslat()
        if (!alive) return
        if (!bilgi) {
          /**
           * Jeton geçersiz: uyarıyı göster ama AKIŞTAN ÇIKMA.
           *
           * Önceki hali burada duruyordu ve kalibrasyon hiç çalışmıyordu;
           * ekran ne tanıtımı ne uyarıyı çizebiliyor, boş bir "0 deneme"
           * satırında kalıyordu. Çocuğun karşısına çıkabilecek en kötü
           * ekran: hiçbir şey söylemeyen bir ekran.
           */
          setJetonHatasi(true)
        } else {
          sunucuRef.current = s
          setBagli(bilgi)
        }
      }

      const c = await calibrate()
      if (!alive) return
      setCal(c)
      if (sunucuRef.current) {
        void sunucuRef.current.cihazBilgisi(deviceInfo(c), Number(c.refreshHz.toFixed(2)))
      }
      setPhase('intro')
    }

    void hazirla()
    return () => {
      alive = false
    }
  }, [])

  const show = useCallback(() => {
    if (stimRef.current) stimRef.current.style.visibility = 'visible'
  }, [])
  const hide = useCallback(() => {
    if (stimRef.current) stimRef.current.style.visibility = 'hidden'
  }, [])

  /* ---------------- oturum döngüsü ---------------- */
  const start = useCallback(() => {
    /**
     * Tohum bağlı modda SUNUCUDAN gelir (ölçümün kimliği).
     *
     * Böylece aynı çocuğun ikinci ölçümü farklı uyarıcılar alır ve
     * gerekirse aynı ölçüm birebir yeniden kurulabilir. Gösterim modunda
     * zaman damgası yeterli.
     */
    saltRef.current = bagli ? bagli.salt : 'demo-' + Date.now()
    startedAtRef.current = Date.now()
    sessionRef.current = new AssessmentSession({
      form: bagli ? bagli.form : 'A',
      salt: saltRef.current,
      /**
       * Uygulayıcı görev seçtiyse yalnızca onlar koşulur.
       *
       * Sunucu listeyi doğrulayarak gönderiyor; burada tekrar
       * süzmüyoruz. Boş gelirse tam set, çünkü `only: []` motorda
       * hiç görev koşturmaz ve çocuk boş ekranla kalırdı.
       */
      ...(bagli?.gorevler && bagli.gorevler.length > 0
        ? { only: bagli.gorevler as TaskCode[] }
        : {}),
    })
    setPhase('running')
  }, [bagli])

  /** Ekranı göster ve kullanıcının devam demesini bekle */
  const waitForUser = useCallback(async () => {
    await new Promise<void>((resolve) => {
      gateRef.current = resolve
    })
    gateRef.current = null
  }, [])

  useEffect(() => {
    if (phase !== 'running' || !cal) return
    let cancelled = false

    /** Tek bir denemeyi koşturur (alıştırmada da gerçekte de aynı yol) */
    const playTrial = async (s: TrialSpec) => {
      setSpec(s)
      setPicked([])
      pickedRef.current = []
      if (!(await framesOrStall(2))) {
        setPaused(true)
        await new Promise<void>((resolve) => {
          resumeRef.current = resolve
        })
        setPaused(false)
        await framesOrStall(2)
      }
      const run =
        s.expect === 'sequence'
          ? await runSequenceTrial(s, cal, setFlash, pickedRef, setPicked, show, hide)
          : await runTrial(cal, {
              show,
              hide,
              durationMs: s.durationMs,
              responseWindowMs: s.responseWindowMs,
              accept: acceptFor(s),
              holdOnResponseMs: ACK_MS,
              onResponse: () => setAck(true),
            })
      setAck(false)
      return run
    }

    /** Alıştırma bloğu: puanlanmaz, kaydedilmez, geri bildirim verir */
    const runPractice = async (task: TaskCode) => {
      const specs = practiceTrials(task, { salt: saltRef.current, count: 6 })
      let correct = 0
      for (const s of specs) {
        if (cancelled) break
        const run = await playTrial(s)
        const j = judge(s, {
          index: s.index,
          stimulusAtMs: run.stimulusAtMs,
          responseAtMs: run.responseAtMs,
          response: run.response ?? undefined,
          framesDropped: run.framesDropped,
          frameMs: run.frameMs,
          lagMs: run.lagMs,
          hidden: run.hidden,
          stalled: run.stalled,
        })
        if (j.correct) correct++

        /**
         * Geri bildirim yalnızca ÖĞRETTİĞİ zaman gösterilir.
         *
         * Hedef olmayan uyarıcıda basmamak doğru cevaptır ama çocuk
         * hiçbir şey yapmamıştır; her harften sonra tik çıkarmak ona bir
         * şey öğretmez, yalnızca ekranı meşgul eder ve "tik gördüm, demek
         * ki iyi gidiyorum" gibi yanlış bir izlenim bırakır.
         *
         * Gösterilenler: doğru tepki (pekiştirme), yanlış tepki ve
         * kaçırılan hedef (düzeltme). Doğru bekleme sessizce geçilir.
         */
        const fb = practiceFeedback(s, j.outcome)
        if (fb) {
          setFeedback(fb)
          await wait(fb.text ? 1100 : 700)
          setFeedback(null)
        }
        await wait(ITI_MS)
      }
      setSpec(null)
      return { correct, total: specs.length }
    }

    const runCountdown = async () => {
      for (let i = 3; i > 0; i--) {
        setCountdown(i)
        await wait(650)
      }
      setCountdown(null)
    }

    const loop = async () => {
      const session = sessionRef.current!
      let lastTask: TaskCode | null = null
      let lastN: number | null = null

      while (!cancelled) {
        const next = session.next()
        if (!next) break

        // Yeni görev: tanıt, alıştır, hazırla
        if (next.task !== lastTask) {
          lastTask = next.task
          setGate({ kind: 'intro', task: next.task })
          await waitForUser()
          if (cancelled) return

          setGate({ kind: 'practice', task: next.task })
          const pr = await runPractice(next.task)
          if (cancelled) return

          setGate({ kind: 'ready', task: next.task, ...pr })
          await waitForUser()
          if (cancelled) return

          setGate(null)
          lastN = next.task === 'nback' ? next.level : null
          await runCountdown()
          if (cancelled) return
        }

        /**
         * n-back'te kural oyun ortasında değişiyor (merdiven n'i 1'den
         * 4'e çıkarıyor). Söylemeden değiştirmek görevi imkânsız kılar:
         * çocuk hâlâ eski kurala göre oynar ve veriler belleği değil
         * şaşkınlığı ölçer.
         */
        if (next.task === 'nback' && lastN !== null && next.level !== lastN) {
          lastN = next.level
          setSpec(null)
          setGate({ kind: 'kuraldegisti', n: next.level })
          await wait(2600)
          setGate(null)
          if (cancelled) return
          await runCountdown()
          if (cancelled) return
        }

        const run = await playTrial(next)
        if (cancelled) return
        const kayit = session.submit({
          index: next.index,
          stimulusAtMs: run.stimulusAtMs,
          responseAtMs: run.responseAtMs,
          response: run.response ?? undefined,
          framesDropped: run.framesDropped,
          frameMs: run.frameMs,
          lagMs: run.lagMs,
          hidden: run.hidden,
          stalled: run.stalled,
        })
        setProgress({ done: session.trials.length })

        /**
         * Sunucuya gönderim BEKLENMİYOR.
         *
         * Denemenin sonunda ağ yanıtını beklemek, bir sonraki uyarıcıyı
         * geciktirir ve ölçtüğümüz süreyi bozar. Deneme yerele yazılıyor,
         * gönderim arka planda yapılıyor; ağ giderse ölçüm devam ediyor.
         */
        const sunucu = sunucuRef.current
        if (sunucu) {
          void sunucu.ekle({
            taskCode: kayit.task,
            domain: kayit.domain,
            trialIndex: kayit.index,
            stimulusAtMs: kayit.stimulusAtMs,
            responseAtMs: kayit.responseAtMs,
            isTarget: kayit.isTarget,
            difficultyLevel: kayit.level,
            framesDropped: kayit.framesDropped,
            presentationLagMs: kayit.presentationLagMs,
            outcome: kayit.outcome,
            stimulus: kayit.stimulus,
          })
          setGonderimDurumu({ bekleyen: sunucu.bekleyen, bagli: sunucu.bagliMi })
        }

        // Denemeler arası kısa boşluk.
        //
        // isiMs KULLANILMIYOR: motorun responseWindowMs değeri zaten
        // süre + ISI toplamı, yani aralık denemenin içinde. Buraya bir de
        // isiMs koyunca denemeler iki kat yavaşlıyordu.
        await wait(ITI_MS)
      }

      if (cancelled) return
      const r = session.result()
      setScores(r.scores)
      setSummary({ trials: r.trialCount, invalidRatio: r.invalidRatio, ok: r.trustworthy })
      setSpec(null)

      // Bağlı modda kalanları gönder ve ölçümü kapat
      const sunucu = sunucuRef.current
      if (sunucu) {
        setPhase('gonderiliyor')
        const sonuc = await sunucu.bitir()
        if (cancelled) return
        setGonderim(sonuc)
      }
      setPhase('done')
    }

    void loop()
    return () => {
      cancelled = true
    }
  }, [phase, cal, show, hide, waitForUser])

  /* ---------------- ekranlar ---------------- */

  if (phase === 'gonderiliyor') {
    return (
      <div className="stage">
        <div className="note">Sonuçlar gönderiliyor…</div>
      </div>
    )
  }

  if (phase === 'calibrating') {
    return (
      <div className="stage">
        <div className="note">Ekran ölçülüyor…</div>
      </div>
    )
  }

  if (phase === 'intro' && cal) {
    // Üç ayrı başarısızlık: akış durdu, hız yetersiz, ya da akış sürüyor
    // ama arada uzun kopmalar var. Üçü de ölçümü güvenilmez kılar.
    const bad = cal.stalled || cal.refreshHz < 45 || cal.lostMs > 200
    return (
      <div className="menu">
        <h1>Ölçüme hazır mısın?</h1>
        <p>
          Altı kısa oyun var. Her biri birkaç dakika sürüyor. Hızlı ve dikkatli ol —
          ama acele edip yanlış basma.
        </p>
        <div className="card" style={{ cursor: 'default' }}>
          <div className="t">Ekran kontrolü</div>
          <div className="d">
            {cal.refreshHz.toFixed(1)} Hz · kare {cal.frameMs.toFixed(2)} ms ·{' '}
            {cal.stalled ? 'kare akışı durdu' : `kayıp ${cal.lostMs.toFixed(0)} ms`}
          </div>
          {bad && (
            <div className="d" style={{ color: 'var(--bad)' }}>
              Bu cihazda ölçüm güvenilir olmayabilir. Pencereyi öne alıp tekrar deneyin.
            </div>
          )}
        </div>
        {jetonHatasi && (
          <div className="uyari">
            Bu bağlantı geçersiz ya da süresi dolmuş. Uygulayıcından yeni bir bağlantı
            iste. İstersen yine de deneyebilirsin ama sonuçlar kaydedilmez.
          </div>
        )}
        {bagli && (
          <p className="note" style={{ textAlign: 'left' }}>
            Sonuçlar uzmanına gönderilecek. Form {bagli.form}.
          </p>
        )}
        <button className="primary" onClick={start}>
          Başla
        </button>
        <button onClick={onExit}>Geri</button>
      </div>
    )
  }

  /**
   * Bağlı modda çocuğa RAPOR GÖSTERİLMEZ.
   *
   * Rapor klinik bir belgedir ve yorumu uzmana aittir. Çocuğun "d' =
   * 2,19" gibi bir sayıyı görmesi ne işe yarar, ne de adildir; kötü
   * gördüğünü sandığı bir sayı sonraki ölçümü de etkiler. Çocuk
   * "bitti, teşekkürler" görür, rapor psikoloğun paneline gider.
   */
  if (phase === 'done' && bagli) {
    return (
      <div className="menu">
        <h1>Bitti</h1>
        <p style={{ fontSize: 18 }}>Teşekkürler, hepsini tamamladın.</p>
        {gonderim?.tamam ? (
          <p className="note" style={{ textAlign: 'left' }}>
            Sonuçlar uzmanına gönderildi. Bu ekranı kapatabilirsin.
          </p>
        ) : (
          <div className="uyari">
            {gonderim && gonderim.eksik > 0
              ? `${gonderim.eksik} deneme henüz gönderilemedi. Bu sayfayı KAPATMA; bağlantı gelince kendiliğinden gönderilecek.`
              : 'Gönderim tamamlanamadı. Bu sayfayı kapatmadan uygulayıcına haber ver.'}
          </div>
        )}
        <button className="primary" onClick={onExit}>
          Kapat
        </button>
      </div>
    )
  }

  if (phase === 'done') {
    const rows = scores ?? []
    // Alanlar her raporda aynı sırada; "önce dikkat, sonra bellek" gibi
    // okunabilir bir düzen, veri geliş sırası değil
    const domains = DOMAIN_ORDER.filter((d) => rows.some((r) => r.domain === d))
    const dakika = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 60000))

    return (
      <div className="result rapor">
        <div className="rapor-bas">
          <div>
            <h2 style={{ margin: 0 }}>Ölçüm raporu</h2>
            <p className="note" style={{ textAlign: 'left', margin: '4px 0 0' }}>
              nrg-core 1.0 · form A · {new Date().toLocaleDateString('tr-TR')} ·{' '}
              {summary?.trials ?? 0} deneme · ~{dakika} dk
            </p>
          </div>
          {summary && (
            <span className={'badge ' + (summary.ok ? 'ok' : 'bad')}>
              {summary.ok ? 'güvenilir' : 'tekrarlanmalı'}
            </span>
          )}
        </div>

        {summary && !summary.ok && (
          <p className="uyari">
            Denemelerin %{(summary.invalidRatio * 100).toFixed(0)} kadarı teknik nedenlerle
            geçersiz sayıldı. Bu ölçüm yorumlanmamalı, tekrarlanmalı.
          </p>
        )}

        {domains.map((d) => {
          const satirlar = rows.filter((r) => r.domain === d)
          const gorevler = [...new Set(satirlar.map((r) => r.task).filter(Boolean))] as string[]
          const deneme = satirlar.reduce((a, r) => Math.max(a, r.trialsUsed), 0)
          return (
            <div className="alan" key={d}>
              <div className="alan-ad">{DOMAIN_LABEL[d] ?? d}</div>
              <div className="alan-yorum">{yorumla(d, rows)}</div>
              <div className="alan-olcut">
                {satirlar.map((r, i) => (
                  <span key={i}>
                    {metricLabel(r.metric, r.task)}:{' '}
                    <b>
                      {bicim(r.value, r.unit, r.metric)} {BIRIM[r.unit] ?? r.unit}
                    </b>
                  </span>
                ))}
              </div>
              {/* Bu sayılar nereden geliyor: rapora güvenmek için gerekli */}
              <div className="alan-kaynak">
                Dayanak: {gorevler.map((g) => TASK_ADI[g as TaskCode] ?? g).join(', ')} · analize
                giren{' '}
                {deneme} deneme
              </div>
            </div>
          )
        })}

        <p className="kisit">
          <b>Kısıtlar.</b> {KISITLAR}
        </p>

        <p className="note" style={{ textAlign: 'left' }}>
          Bu bir gösterimdir: sonuçlar kaydedilmedi, sunucuya gönderilmedi.
        </p>

        <div className="controls">
          <button className="primary" onClick={() => window.print()}>
            Raporu yazdır
          </button>
          <button onClick={onExit}>Bitir</button>
        </div>
      </div>
    )
  }

  /* ---------------- görev geçiş ekranları ---------------- */

  if (gate?.kind === 'kuraldegisti') {
    const k = nbackKural(gate.n)
    return (
      <div className="overlay">
        <div style={{ display: 'grid', gap: 14, justifyItems: 'center', padding: 24, maxWidth: 520 }}>
          <div className="badge" style={{ fontSize: 14 }}>kural değişti</div>
          <div style={{ fontSize: 26, fontWeight: 700, textAlign: 'center' }}>{k.kisa}</div>
          <div style={{ fontSize: 16, textAlign: 'center', color: 'var(--muted)' }}>{k.uzun}</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{k.tut}</div>
          <div style={{ fontSize: 18, letterSpacing: 1 }}>{k.ornek}</div>
        </div>
      </div>
    )
  }

  if (gate?.kind === 'intro') {
    const a = anlatimFor(gate.task)
    return (
      <div className="menu anlatim">
        <p className="note" style={{ textAlign: 'left', margin: 0 }}>Yeni oyun</p>
        <h1 style={{ marginTop: 2 }}>{TASK_LABEL[gate.task]}</h1>
        <p className="kural">{a.kisa}</p>

        <ol className="adimlar">
          {a.adimlar.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>

        <div className="ornek">
          <span>Örnek</span>
          <b>{a.ornek}</b>
        </div>

        <div className="dikkat">
          <span>Dikkat</span>
          <b>{a.dikkat}</b>
        </div>

        <p className="note" style={{ textAlign: 'left' }}>
          Şimdi birlikte deneyeceğiz. Deneme sırasında her seferinde doğru mu yaptığını
          göstereceğim. Bu bölüm sayılmıyor, rahat ol.
        </p>
        <button className="primary" onClick={() => gateRef.current?.()}>
          Denemeye başla
        </button>
      </div>
    )
  }

  if (gate?.kind === 'ready') {
    const oran = gate.total ? gate.correct / gate.total : 0
    return (
      <div className="menu">
        <h1>{oran >= 0.6 ? 'Anladın' : 'Biraz daha dikkat'}</h1>
        <p style={{ fontSize: 17 }}>
          Denemede {gate.total} sorudan {gate.correct} tanesini doğru yaptın.
        </p>
        <p className="note" style={{ textAlign: 'left' }}>
          {oran >= 0.6
            ? 'Şimdi gerçeği başlıyor. Bu bölüm sayılacak, acele etme ama oyalanma da.'
            : 'Yönergeyi bir daha oku, sonra başlayalım. Zorlanman normal.'}
        </p>
        <p className="note" style={{ textAlign: 'left' }}>
          {anlatimFor(gate.task).kisa}
        </p>
        <button className="primary" onClick={() => gateRef.current?.()}>
          Hazırım
        </button>
      </div>
    )
  }

  /* ---------------- koşu ekranı ---------------- */
  const task = spec?.task
  const seq = (spec?.stimulus as Record<string, unknown> | undefined)?.['sequence'] as
    | string[]
    | undefined
  const gridSize = Number((spec?.stimulus as Record<string, unknown> | undefined)?.['gridSize'] ?? 4)

  return (
    <div className="app">
      <div className="bar">
        <span>
          <b>{task ? TASK_LABEL[task] : ''}</b>
          {gate?.kind === 'practice' && (
            <span className="badge" style={{ marginLeft: 8 }}>deneme</span>
          )}
        </span>
        <span>
          {gate?.kind === 'practice' ? 'alıştırma' : `${progress.done} deneme`}
          {/* Bağlantı koptuysa sessizce geçme: bekleyen veri olduğunu
              uygulayıcı görmeli */}
          {bagli && !gonderimDurumu.bagli && (
            <span className="badge bad" style={{ marginLeft: 8 }}>
              bağlantı yok · {gonderimDurumu.bekleyen} bekliyor
            </span>
          )}
        </span>
      </div>

      {spec && <SearchTarget spec={spec} />}

      <div className="stage">
        {/* Sabitleme işareti kaldırıldı: uyarıcı akarken ortada duran bir
            "+" hem görsel gürültü hem de harfle karışabiliyor. Harfler
            gelip gidiyor, bakılacak yer zaten orası. */}
        {ack && <div className="ack" />}
        <div className="stim" ref={stimRef}>
          {spec?.expect === 'sequence' ? (
            <div
              className="sgrid"
              style={{ gridTemplateColumns: `repeat(${gridSize}, auto)` }}
            >
              {Array.from({ length: gridSize * gridSize }, (_, i) => (
                <div
                  key={i}
                  className={
                    'c tap' +
                    (flash === i ? ' on' : '') +
                    (picked.includes(String(i)) ? ' picked' : '')
                  }
                  data-cell={i}
                />
              ))}
            </div>
          ) : (
            <Stimulus spec={spec} />
          )}
        </div>
      </div>

      {countdown !== null && (
        <div className="overlay">
          <div style={{ fontSize: 96, fontWeight: 700 }}>{countdown}</div>
        </div>
      )}

      {feedback && (
        <div className="overlay" style={{ background: 'rgba(255,255,255,.72)', pointerEvents: 'none' }}>
          <div style={{ display: 'grid', justifyItems: 'center', gap: 10, padding: 24 }}>
            <div style={{ fontSize: 110, lineHeight: 1, color: feedback.ok ? 'var(--ok)' : 'var(--bad)' }}>
              {feedback.ok ? '✓' : '✗'}
            </div>
            {feedback.text && (
              <div style={{ fontSize: 18, textAlign: 'center', maxWidth: 420 }}>{feedback.text}</div>
            )}
          </div>
        </div>
      )}

      {paused && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,.96)',
            display: 'grid',
            placeItems: 'center',
            gap: 16,
            padding: 24,
            zIndex: 10,
          }}
        >
          <div className="big">Ekran çizimi durdu</div>
          <p className="note">
            Pencere arkada kaldığında tarayıcı çizmeyi durduruyor ve süreler ölçülemiyor.
            Pencereyi öne alıp devam edin — bu sırada deneme yapılmadı.
          </p>
          <button
            className="primary"
            onClick={() => {
              resumeRef.current?.()
              resumeRef.current = null
            }}
          >
            Devam et
          </button>
        </div>
      )}

      <p className="note">{hintFor(spec)}</p>
      <div className="controls">{responseControls(spec)}</div>
    </div>
  )
}

/* ------------------------------------------------------------------ */

function responseControls(spec: TrialSpec | null) {
  if (!spec) return null
  const kind = (spec.stimulus as Record<string, unknown>)['kind']

  if (spec.expect === 'respond' || spec.expect === 'withhold') {
    return (
      <button className="primary wide" data-resp="response">
        BAS
      </button>
    )
  }
  if (kind === 'flanker') {
    return (
      <>
        <button className="wide" data-resp="sol">
          ◀
        </button>
        <button className="wide" data-resp="sag">
          ▶
        </button>
      </>
    )
  }
  if (kind === 'symbol_search') {
    return (
      <>
        <button className="wide" data-resp="var">
          VAR
        </button>
        <button className="wide" data-resp="yok">
          YOK
        </button>
      </>
    )
  }
  if (kind === 'rule_switch') {
    const colors = ['mavi', 'kirmizi', 'yesil', 'sari']
    const shapes = ['daire', 'kare', 'ucgen', 'yildiz', 'altigen', 'kalp']
    return (
      <>
        {colors.map((c) => (
          <button key={c} data-resp={c} style={{ minWidth: 56 }}>
            <span
              style={{
                display: 'inline-block',
                width: 22,
                height: 22,
                borderRadius: 6,
                background:
                  c === 'mavi' ? '#3b74e8' : c === 'kirmizi' ? '#d94b4b' : c === 'yesil' ? '#3fae5a' : '#d9b63b',
              }}
            />
          </button>
        ))}
        {shapes.map((s) => (
          <button key={s} data-resp={s} style={{ minWidth: 56 }}>
            {SHAPE_GLYPH[s]}
          </button>
        ))}
      </>
    )
  }
  if (spec.expect === 'sequence') {
    return <p className="note">Kutulara sırayla dokun.</p>
  }
  return null
}

/**
 * Alıştırmada ne zaman geri bildirim verilir.
 *
 * `null` dönerse hiçbir şey gösterilmez ve deneme sessizce geçilir.
 */
function practiceFeedback(
  spec: TrialSpec,
  outcome: string | null,
): { ok: boolean; text?: string } | null {
  switch (outcome) {
    case 'correct_rejection':
      // Doğru bekleme: yapılacak bir şey yoktu, pekiştirilecek bir şey de yok
      return null
    case 'hit':
      return { ok: true }
    case 'false_alarm':
      return spec.expect === 'withhold'
        ? { ok: false, text: 'Bu aradığın değildi — basmayacaktın' }
        : { ok: false, text: 'Yanlış seçtin' }
    case 'miss':
      return { ok: false, text: 'Aradığın buydu — basacaktın' }
    case 'omission':
      return { ok: false, text: 'Cevap vermedin' }
    default:
      return null
  }
}

/** Olaydan cevabı çıkarır: düğmelerde data-resp, klavyede ok ve boşluk */
function acceptFor(spec: TrialSpec) {
  return (ev: PointerEvent | KeyboardEvent): string | null => {
    if (ev instanceof KeyboardEvent) {
      if (ev.repeat) return null
      if (spec.expect === 'respond' || spec.expect === 'withhold') {
        return ev.key === ' ' || ev.key === 'Enter' ? 'response' : null
      }
      const kind = (spec.stimulus as Record<string, unknown>)['kind']
      if (kind === 'flanker') {
        if (ev.key === 'ArrowLeft') return 'sol'
        if (ev.key === 'ArrowRight') return 'sag'
      }
      if (kind === 'symbol_search') {
        if (ev.key === 'ArrowLeft') return 'var'
        if (ev.key === 'ArrowRight') return 'yok'
      }
      return null
    }
    const el = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-resp]')
    return el?.dataset['resp'] ?? null
  }
}

async function runSequenceTrial(
  spec: TrialSpec,
  cal: Calibration,
  setFlash: (i: number | null) => void,
  pickedRef: { current: string[] },
  setPicked: (v: string[]) => void,
  show: () => void,
  hide: () => void,
): Promise<TrialRun> {
  const s = spec.stimulus as Record<string, unknown>
  const sequence = s['sequence'] as string[]
  const flashMs = Number(s['flashMs'] ?? 500)

  show()
  const onset = await nextFrame()

  // Gösterim: kutular sırayla yanar
  for (const cell of sequence) {
    setFlash(Number(cell))
    await wait(flashMs)
    setFlash(null)
    await wait(160)
  }

  // Geri verme: çocuk kutulara sırayla dokunur
  const picked: string[] = []
  let lastAt = 0
  await new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, spec.responseWindowMs)
    const onTap = (ev: PointerEvent) => {
      const el = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-cell]')
      if (!el) return
      picked.push(el.dataset['cell']!)
      pickedRef.current = [...picked]
      setPicked([...picked])
      lastAt = ev.timeStamp
      if (picked.length >= sequence.length) {
        window.clearTimeout(timer)
        document.removeEventListener('pointerdown', onTap)
        resolve()
      }
    }
    document.addEventListener('pointerdown', onTap)
  })

  // Son dokunuştan sonra kısa bir duraklama: seçim ekranda kalsın ki
  // çocuk ne yaptığını görsün. Anında geçmek "dokunmadım galiba"
  // hissi veriyordu.
  if (picked.length > 0) await wait(650)

  setFlash(null)

  /**
   * İşaretler BURADA temizleniyor, yeni deneme başlarken değil.
   *
   * Önceki hali, bir sonraki ızgara belirirken eski işaretleri siliyordu:
   * çocuk bir an dolu kutular görüyor, sonra hepsi birden sönüyordu ve
   * "nereye dokunacağım" diye duraksıyordu. Temizlik denemeler arası
   * boşlukta, ekran kapalıyken olmalı.
   */
  setPicked([])
  pickedRef.current = []
  await wait(120)

  hide()

  return {
    stimulusAtMs: onset,
    responseAtMs: picked.length > 0 ? lastAt : null,
    response: picked.length > 0 ? picked : null,
    framesDropped: 0,
    lagMs: 0,
    frameMs: cal.frameMs,
    hidden: document.visibilityState !== 'visible',
    stalled: false,
  }
}

/** Denemeler arası görsel boşluk (ms) */
const ITI_MS = 250

/** Tepki alındı halkasının ekranda kalma süresi */
const ACK_MS = 340

function nextFrame(): Promise<number> {
  return new Promise((r) => requestAnimationFrame(r))
}

/**
 * n kare bekler; kare akışı durursa false döner.
 *
 * Zaman aşımı zorunlu: arkaya düşmüş pencerede rAF hiç tetiklenmiyor ve
 * zaman aşımsız bir bekleme ölçümü sessizce kilitliyor.
 */
async function framesOrStall(n: number): Promise<boolean> {
  for (let i = 0; i < n; i++) {
    const ok = await Promise.race([
      nextFrame().then(() => true),
      wait(STALL_TIMEOUT_MS).then(() => false),
    ])
    if (!ok) return false
  }
  return true
}
function wait(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms))
}
